/**
 * services/modalGuard.ts · v353 通用 modal ✕ 强插 (复用 v200 v199.93)
 *
 * 防困用户: 任何 fixed > 280×200 z>100 modal 无 close button → 强插红 ✕
 *
 * 监听:
 *   - MutationObserver body subtree:false addedNodes
 *   - 新 fixed 大 modal 检测无 ✕ → 注入
 *
 * Esc 全局兜底关闭最顶层 modal
 */

const KNOWN_CLOSE_SELECTORS = [
  '.tn-confirm-btn--cancel',
  '.tn-drawer-close',
  '[data-act="cancel"]',
  '[data-act="close"]',
  '[aria-label="close"]',
  'button[onclick*="close"]',
];

function hasCloseButton(el: HTMLElement): boolean {
  for (const sel of KNOWN_CLOSE_SELECTORS) {
    if (el.querySelector(sel)) return true;
  }
  // ✕ 或 × 文本 button
  const btns = el.querySelectorAll<HTMLButtonElement>('button');
  for (const b of Array.from(btns)) {
    const txt = (b.textContent || '').trim();
    if (txt === '✕' || txt === '×' || txt === 'X' || /关闭|close|hủy|đóng/i.test(txt)) return true;
  }
  return false;
}

function isLargeFixedModal(el: HTMLElement): boolean {
  const cs = getComputedStyle(el);
  if (cs.position !== 'fixed' && cs.position !== 'absolute') return false;
  const z = parseInt(cs.zIndex || '0');
  if (z < 100) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width < 280 || rect.height < 200) return false;
  // 不是 toast (toast 通常右下角小 + 自动关)
  if (el.classList.contains('tn-toast') || el.id === 'tn-toast-container') return false;
  return true;
}

function injectCloseButton(modal: HTMLElement): void {
  if (modal.dataset.tnModalGuardInjected) return;
  modal.dataset.tnModalGuardInjected = '1';
  if (hasCloseButton(modal)) return;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = '关闭 / Đóng (Esc)';
  btn.setAttribute('aria-label', 'close');
  btn.style.cssText = `
    position:absolute; top:8px; right:8px; z-index:9999999;
    width:32px; height:32px; border-radius:50%;
    background:#dc2626; color:#fff; border:0; cursor:pointer;
    font-size:18px; font-weight:700;
    display:flex; align-items:center; justify-content:center;
    box-shadow:0 2px 6px rgba(0,0,0,0.18);
    transition:background 0.15s, transform 0.1s;
  `;
  btn.textContent = '✕';
  btn.onmouseenter = () => { btn.style.background = '#b91c1c'; btn.style.transform = 'scale(1.06)'; };
  btn.onmouseleave = () => { btn.style.background = '#dc2626'; btn.style.transform = 'scale(1)'; };
  btn.onclick = () => {
    modal.style.display = 'none';
    setTimeout(() => { try { modal.remove(); } catch {} }, 100);
  };

  // 确保 modal position 可放 absolute 子元素
  const cs = getComputedStyle(modal);
  if (cs.position === 'static') modal.style.position = 'relative';

  modal.appendChild(btn);
}

let observer: MutationObserver | null = null;

export function initModalGuard(): void {
  if (observer) return;

  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of Array.from(m.addedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as HTMLElement;
        // 延迟 200ms 等 modal 渲染完
        setTimeout(() => {
          if (!document.body.contains(el)) return;
          if (isLargeFixedModal(el)) injectCloseButton(el);
        }, 200);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: false });

  // Esc 全局兜底关闭最顶层 modal
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // 找最顶 z-index fixed modal (排除 toast / drawer / cheatsheet / our own confirm)
    const candidates = Array.from(document.body.children).filter((el) => {
      const html = el as HTMLElement;
      if (!html.style) return false;
      const cs = getComputedStyle(html);
      return cs.position === 'fixed' && parseInt(cs.zIndex || '0') >= 100 && isLargeFixedModal(html);
    });
    const top = candidates.sort((a, b) => parseInt(getComputedStyle(b as HTMLElement).zIndex || '0') - parseInt(getComputedStyle(a as HTMLElement).zIndex || '0'))[0] as HTMLElement | undefined;
    if (top && !top.classList.contains('tn-confirm-overlay') && !top.classList.contains('tn-cheat-backdrop')) {
      const closeBtn = top.querySelector<HTMLButtonElement>('button[aria-label="close"], .tn-drawer-close, [data-act="cancel"]');
      if (closeBtn) { closeBtn.click(); e.preventDefault(); }
    }
  });
}
